import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { stringSchema } from 'src/Configuration/validation/rules'
import { boolean, object, ObjectSchema } from 'yup'
import { SHORT_FIELD_LENGTH, LONG_FIELD_LENGTH, COMPANY_FIELD_LENGTH, ZIP_CODE_FIELD_LENGTH } from 'src/Configuration/validation/consts'
import { ONLY_DIGITS_REGEX } from './regex-patterns'
import { CompanyAddressFormData, UserAddressFormData } from '../../types'
import { Address } from 'src/Libs/user/types'
import { ValidationMessageKeys } from 'src/Configuration/validation'
import { AddressFormValidationData, InProgressAddressValidation } from '../../types/address'

export const PARTNER_NUMBER_FIELD_LENGTH = 12

/**
 * This hook provides the validation schema for addresses using the custom defined yup-validation-rules
 */
const useAddressValidation = (): {
  inProgressValidationSchema: ObjectSchema<InProgressAddressValidation>
  finalValidationSchema: ObjectSchema<AddressFormValidationData>
} => {
  const { t } = useCustomTranslation()
  const stringSchemaWithTranslation = stringSchema(t)

  const customerAddress: ObjectSchema<Partial<UserAddressFormData>> = object().shape({
    firstName: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }),
    lastName: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }),
    addressAndHouseNumber: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }),
    zipCode: stringSchemaWithTranslation({
      maxLength: ZIP_CODE_FIELD_LENGTH,
    }),
    city: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }),
    country: stringSchemaWithTranslation({ maxLength: SHORT_FIELD_LENGTH }),
  })

  /* note - address, zipcode, city and country are prefilled from company profile */
  const companyAddress: ObjectSchema<Partial<CompanyAddressFormData>> = object().shape({
    companyName: stringSchemaWithTranslation({ maxLength: COMPANY_FIELD_LENGTH }).required(),
    addressAndHouseNumber: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }),
    zipCode: stringSchemaWithTranslation({
      maxLength: ZIP_CODE_FIELD_LENGTH,
    }),
    city: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }),
    country: stringSchemaWithTranslation({ maxLength: SHORT_FIELD_LENGTH }),
  })

  const deviatingAddress: ObjectSchema<Partial<Address>> = object().shape({
    addressAndHouseNumber: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }),
    zipCode: stringSchemaWithTranslation({
      maxLength: ZIP_CODE_FIELD_LENGTH,
    }),
    city: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }),
    country: stringSchemaWithTranslation({ maxLength: SHORT_FIELD_LENGTH }),
  })

  const partnerNumber = stringSchemaWithTranslation({
    regexp: { rule: ONLY_DIGITS_REGEX, errorMessage: t(ValidationMessageKeys.onlyDigits) },
  }).when('partnerNumberEnabled', {
    is: true,
    then: schema =>
      schema
        .required()
        .length(PARTNER_NUMBER_FIELD_LENGTH, t('validation.error-message.invalid-length', { value: PARTNER_NUMBER_FIELD_LENGTH }))
        .test('partnerNumber-validity', t('validation.error-message.installation-statement.partner-number-invalid'), function (value) {
          const { partnerNumberEnabled, partnerNumberValid } = this.parent
          /* Skip validation if partnerNumberEnabled is false */
          if (!partnerNumberEnabled || !value) {
            return true
          }
          /* Skip validation if  length is invalid */
          if (value.length !== PARTNER_NUMBER_FIELD_LENGTH) {
            return true
          }
          /* Validate partnerNumberValid only when partnerNumber is of valid length */
          return partnerNumberValid
        }),
  })

  const inProgressValidationSchema = object().shape({
    customerAddress: customerAddress,
    deviatingAddressEnabled: boolean().required(),
    deviatingAddress: deviatingAddress.when('deviatingAddressEnabled', {
      is: false,
      then: () => object(),
    }),
    companyAddress: companyAddress,
    partnerNumber: partnerNumber,
    partnerNumberEnabled: boolean().required(),
  })

  const customerAddressRequired: ObjectSchema<UserAddressFormData> = object().shape({
    firstName: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }).required(),
    lastName: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }).required(),
    addressAndHouseNumber: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }).required(),
    zipCode: stringSchemaWithTranslation({
      maxLength: ZIP_CODE_FIELD_LENGTH,
    }).required(),
    city: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }).required(),
    country: stringSchemaWithTranslation({ maxLength: SHORT_FIELD_LENGTH }).required(),
  })

  const deviatingAddressRequired: ObjectSchema<Address> = object().shape({
    addressAndHouseNumber: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }).required(),
    zipCode: stringSchemaWithTranslation({
      maxLength: ZIP_CODE_FIELD_LENGTH,
    }).required(),
    city: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }).required(),
    country: stringSchemaWithTranslation({ maxLength: SHORT_FIELD_LENGTH }).required(),
  })

  /* note - address, zipcode, city and country are prefilled from company profile */
  const companyAddressRequired: ObjectSchema<CompanyAddressFormData> = object().shape({
    companyName: stringSchemaWithTranslation({ maxLength: COMPANY_FIELD_LENGTH }).required(),
    addressAndHouseNumber: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }).required(),
    zipCode: stringSchemaWithTranslation({
      maxLength: ZIP_CODE_FIELD_LENGTH,
    }).required(),
    city: stringSchemaWithTranslation({
      maxLength: LONG_FIELD_LENGTH,
    }).required(),
    country: stringSchemaWithTranslation({ maxLength: SHORT_FIELD_LENGTH }).required(),
  })

  const finalValidationSchema = object().shape({
    customerAddress: customerAddressRequired,
    deviatingAddressEnabled: boolean().required(),
    deviatingAddress: deviatingAddressRequired.when('deviatingAddressEnabled', { is: false, then: () => object() }).optional(),
    companyAddress: companyAddressRequired,
    partnerNumber: partnerNumber,
    partnerNumberEnabled: boolean().required(),
  })

  return { inProgressValidationSchema, finalValidationSchema }
}

export default useAddressValidation
